import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import SectionHead from "./SectionHead";
import Tilt from "./Tilt";
import { img, inView, rise, stagger } from "./utils";


const pagesOf = (s) => {
  const list = (s.images || []).filter((i) => i?.image);
  if (list.length) return list;
  return s.image ? [{ id: "legacy", image: s.image }] : [];
};


const S = {
  overlay: {
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 999999,
    background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    overscrollBehavior: "contain", touchAction: "none",
  },
  // a 16:9 screen: as wide as fits, 
  box: {
    position: "relative", width: "min(94vw, calc(90vh * 16 / 9))", aspectRatio: "16 / 9",
    background: "#000", borderRadius: 14, overflow: "hidden", boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
  },
  close: {
    position: "absolute", top: 10, right: 10, zIndex: 2, width: 40, height: 40, borderRadius: "50%", border: 0,
    background: "rgba(0,0,0,0.65)", color: "#fff", fontSize: 26, lineHeight: "40px", textAlign: "center", padding: 0, cursor: "pointer",
  },
  scroll: {
    width: "100%", height: "100%", overflowY: "auto", overflowX: "hidden", padding: 0, margin: 0,
    overscrollBehavior: "contain", touchAction: "pan-y", WebkitOverflowScrolling: "touch",
  },
  pic: { display: "block", width: "100%", height: "auto", margin: 0, padding: 0, border: 0, borderRadius: 0 },
  open: { position: "relative", display: "block", width: "100%", padding: 0, border: 0, background: "none", cursor: "pointer" },
  badge: {
    position: "absolute", right: 12, bottom: 12, padding: "4px 11px", borderRadius: 999, fontSize: "0.76rem", fontWeight: 600,
    color: "#fff", background: "rgba(7,19,28,0.72)",
  },
  more: { display: "block", margin: 0, padding: "0 26px 26px", border: 0, background: "none", textAlign: "left", cursor: "pointer", fontWeight: 600, fontSize: "0.92rem", color: "#0b8f98" },
};

/* simple pop-up: the images after the thumbnail in one scrolling 16:9 window, a cross closes it */
const Popup = ({ pages, onClose }) => {
  const overlay = useRef(null);
  const scroller = useRef(null);

  useEffect(() => {
   
    const html = document.documentElement;
    const prevBody = document.body.style.overflow;
    const prevHtml = html.style.overflow;
    document.body.style.overflow = "hidden";
    html.style.overflow = "hidden";

    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);

    // the wheel and touch only ever scroll the images, never the page (also stops smooth-scroll plugins)
    const ov = overlay.current;
    const box = scroller.current;
    const onWheel = (e) => {
      e.stopPropagation();
      if (!box || !box.contains(e.target)) return e.preventDefault();
      const atTop = box.scrollTop <= 0 && e.deltaY < 0;
      const atEnd = box.scrollTop + box.clientHeight >= box.scrollHeight - 1 && e.deltaY > 0;
      if (atTop || atEnd) e.preventDefault();
    };
    let lastY = 0;
    const onTouchStart = (e) => { lastY = e.touches[0].clientY; };
    const onTouchMove = (e) => {
      e.stopPropagation();
      if (!box || !box.contains(e.target)) return e.preventDefault();
      const dy = lastY - e.touches[0].clientY; // > 0 = finger moves up = scrolls down
      lastY = e.touches[0].clientY;
      const atTop = box.scrollTop <= 0 && dy < 0;
      const atEnd = box.scrollTop + box.clientHeight >= box.scrollHeight - 1 && dy > 0;
      if (atTop || atEnd) e.preventDefault();
    };
    ov?.addEventListener("wheel", onWheel, { passive: false });
    ov?.addEventListener("touchstart", onTouchStart, { passive: true });
    ov?.addEventListener("touchmove", onTouchMove, { passive: false });

    return () => {
      document.body.style.overflow = prevBody;
      html.style.overflow = prevHtml;
      document.removeEventListener("keydown", onKey);
      ov?.removeEventListener("wheel", onWheel);
      ov?.removeEventListener("touchstart", onTouchStart);
      ov?.removeEventListener("touchmove", onTouchMove);
    };
  }, [onClose]);

  return createPortal(
    <div ref={overlay} style={S.overlay} data-lenis-prevent onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div style={S.box} role="dialog" aria-modal="true" aria-label="Impact photos">
        <button type="button" style={S.close} onClick={onClose} aria-label="Close" autoFocus>
          ×
        </button>
        <div ref={scroller} style={S.scroll} data-lenis-prevent>
          {pages.map((p, i) => (
            <img key={p.id} src={img(p.image)} alt={`Photo ${i + 1} of ${pages.length}`} style={S.pic} loading={i < 2 ? "eager" : "lazy"} />
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
};

const Impact = ({ stats = [] }) => {
  const [open, setOpen] = useState(null);
  if (!stats.length) return null;

  return (
    <section id="impact" className="au-sec au-sec--blush">
      <div className="au-wrap">
        <SectionHead eyebrow="What we have done" title="Our impact" />
        <motion.ul className="au-impact" variants={stagger} initial="hide" whileInView="show" viewport={inView}>
          {stats.map((s, i) => {
            const pages = pagesOf(s);
            const cover = pages[0]?.image;
            const more = pages.length - 1; // images other than the thumbnail
            const canOpen = more > 0;
            return (
              <Tilt as="li" key={s.id} className="au-impact__item" variants={rise} max={6}>
                <span className="au-impact__no">{String(i + 1).padStart(2, "0")}</span>

                {cover &&
                  (canOpen ? (
                    <button
                      type="button"
                      className="au-impact__img"
                      style={S.open}
                      onClick={() => setOpen(s)}
                      aria-label={`Read more: ${(s.description || "").slice(0, 60)}`}
                    >
                      <img src={img(cover)} alt="" loading="lazy" />
                      <span style={S.badge}>+{more} more</span>
                    </button>
                  ) : (
                    <div className="au-impact__img">
                      <img src={img(cover)} alt="" loading="lazy" />
                    </div>
                  ))}

                <p>{s.description}</p>

                {canOpen && (
                  <button type="button" style={S.more} onClick={() => setOpen(s)}>
                    Read more →
                  </button>
                )}
              </Tilt>
            );
          })}
        </motion.ul>
      </div>

      {open && <Popup pages={pagesOf(open).slice(1)} onClose={() => setOpen(null)} />}
    </section>
  );
};

export default Impact;